import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import { useMarketSegments, useMarketOverview } from '@/hooks/useMarket';
import { useProfileGeneration } from '@/hooks/useProfileGeneration';
import {
  ProfileBuilder,
  DerivedPersonaCard,
  ComparisonSummary,
  CrossTabsView,
} from '@/components/consumers';
import { LoadingSpinner } from '@/components/ui';
import type { ProfileSelections, DerivedPersona, Segment } from '@/types/consumer';

// ============================================================================
// TYPES
// ============================================================================

type TabId = 'cross-tabs' | 'profile-builder' | 'research-results';

interface Tab {
  id: TabId;
  label: { en: string; es: string };
  badge?: { en: string; es: string };
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TABS: Tab[] = [
  {
    id: 'cross-tabs',
    label: { en: 'Cross-Tabs', es: 'Cross-Tabs' },
  },
  {
    id: 'profile-builder',
    label: { en: 'Profile Builder', es: 'Generador de Perfil' },
  },
  {
    id: 'research-results',
    label: { en: 'Research Results', es: 'Resultados de Investigación' },
    badge: { en: 'Client Data', es: 'Datos Cliente' },
  },
];

// ============================================================================
// COMPARE CONSUMERS PAGE
// ============================================================================

export function CompareConsumersPage() {
  const { language } = useLanguage();
  const { selectedClient } = useClient();
  const t = language === 'es';

  // Active tab state - Cross-Tabs is default
  const [activeTab, setActiveTab] = useState<TabId>('cross-tabs');

  // Fetch data
  const { data: segmentsData, isLoading: segmentsLoading } = useMarketSegments(
    selectedClient?.client_id
  );
  const { data: marketData, isLoading: marketLoading } = useMarketOverview(
    selectedClient?.client_id
  );

  const isLoading = segmentsLoading || marketLoading;

  // Transform segments to match our interface
  const segments: Segment[] = useMemo(() => {
    if (!segmentsData?.segments) return [];
    return segmentsData.segments.map((seg: any) => ({
      id: seg.id || seg.name.toLowerCase().replace(/\s+/g, '_'),
      name: seg.name,
      share: seg.share,
      characteristics: seg.characteristics || [],
      price_sensitivity: seg.price_sensitivity ?? 0.5,
      brand_loyalty: seg.brand_loyalty ?? 0.5,
      demographics: {
        age_range: '25-55',
        income: 'C',
        geography: 'National',
      },
    }));
  }, [segmentsData]);

  // Get brands from market overview
  const brands = useMemo(() => {
    if (!marketData?.top_brands) {
      return [{ name: 'Generic Brand', share: 0.5 }];
    }
    return marketData.top_brands;
  }, [marketData]);

  // Profile generation hook
  const { generateProfile, compareProfiles } = useProfileGeneration(segments, language);

  // State for both profiles (Profile Builder tab)
  const [profileA, setProfileA] = useState<DerivedPersona | null>(null);
  const [profileB, setProfileB] = useState<DerivedPersona | null>(null);

  // Handle profile generation
  const handleGenerateA = (selections: ProfileSelections) => {
    if (segments.length > 0) {
      setProfileA(generateProfile(selections));
    }
  };

  const handleGenerateB = (selections: ProfileSelections) => {
    if (segments.length > 0) {
      setProfileB(generateProfile(selections));
    }
  };

  // Calculate comparison when both profiles exist
  const comparison = useMemo(() => {
    if (profileA && profileB) {
      return compareProfiles(profileA, profileB);
    }
    return null;
  }, [profileA, profileB, compareProfiles]);

  // No client selected
  if (!selectedClient) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-text-secondary mb-4">
            {t
              ? 'Selecciona un cliente para ver inteligencia del consumidor.'
              : 'Select a client to view consumer intelligence.'}
          </p>
          <Link
            to="/"
            className="text-primary-400 hover:text-primary-300"
          >
            {t ? '← Ir al inicio' : '← Go to home'}
          </Link>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // No segments available
  if (segments.length === 0) {
    return (
      <div className="max-w-4xl mx-auto py-8 px-4">
        <Link
          to="/understand/consumer-intelligence"
          className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-white mb-6"
        >
          ← {t ? 'Volver a Inteligencia del Consumidor' : 'Back to Consumer Intelligence'}
        </Link>
        <div className="card p-8 text-center">
          <p className="text-text-secondary">
            {t
              ? 'No hay segmentos disponibles para este cliente.'
              : 'No segments available for this client.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-6 px-4">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/understand/consumer-intelligence"
          className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-white mb-2"
        >
          ← {t ? 'Inteligencia del Consumidor' : 'Consumer Intelligence'}
        </Link>
        <h1 className="text-2xl font-bold text-white">
          {t ? 'Inteligencia del Consumidor' : 'Consumer Intelligence'}
        </h1>
        <p className="text-text-secondary mt-1">
          {t
            ? 'Compara marcas, genera perfiles de consumidor y explora datos de investigación.'
            : 'Compare brands, generate consumer profiles, and explore research data.'}
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-surface-700 mb-6">
        <nav className="flex gap-1" aria-label="Tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-text-secondary hover:text-white hover:border-surface-500'
              }`}
            >
              {tab.label[language as 'en' | 'es']}
              {tab.badge && (
                <span className="ml-2 px-1.5 py-0.5 text-xs bg-amber-500/20 text-amber-400 rounded">
                  {tab.badge[language as 'en' | 'es']}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'cross-tabs' && (
        <CrossTabsView
          brands={brands}
          language={language}
        />
      )}

      {activeTab === 'profile-builder' && (
        <ProfileBuilderTab
          brands={brands}
          language={language}
          profileA={profileA}
          profileB={profileB}
          comparison={comparison}
          onGenerateA={handleGenerateA}
          onGenerateB={handleGenerateB}
        />
      )}

      {activeTab === 'research-results' && (
        <ResearchResultsTab language={language} clientId={selectedClient.client_id} />
      )}
    </div>
  );
}

// ============================================================================
// TAB COMPONENTS
// ============================================================================

interface ProfileBuilderTabProps {
  brands: { name: string; share: number }[];
  language: string;
  profileA: DerivedPersona | null;
  profileB: DerivedPersona | null;
  comparison: any;
  onGenerateA: (selections: ProfileSelections) => void;
  onGenerateB: (selections: ProfileSelections) => void;
}

function ProfileBuilderTab({
  brands,
  language,
  profileA,
  profileB,
  comparison,
  onGenerateA,
  onGenerateB,
}: ProfileBuilderTabProps) {
  const t = language === 'es';

  return (
    <div className="space-y-6">
      {/* Description */}
      <p className="text-sm text-text-secondary">
        {t
          ? 'Crea y compara dos perfiles de consumidor basados en demografía.'
          : 'Create and compare two consumer profiles based on demographics.'}
      </p>

      {/* Split Screen - Profile Builders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile A */}
        <div className="space-y-4">
          <ProfileBuilder
            side="A"
            brands={brands}
            onGenerate={onGenerateA}
            language={language}
          />
          {profileA && (
            <DerivedPersonaCard persona={profileA} language={language} />
          )}
        </div>

        {/* Profile B */}
        <div className="space-y-4">
          <ProfileBuilder
            side="B"
            brands={brands}
            onGenerate={onGenerateB}
            language={language}
          />
          {profileB && (
            <DerivedPersonaCard persona={profileB} language={language} />
          )}
        </div>
      </div>

      {/* VS Badge (centered) */}
      {(profileA || profileB) && (
        <div className="flex justify-center -mt-2 mb-4">
          <span className="px-3 py-1 bg-surface-700 rounded-full text-xs font-medium text-text-secondary">
            VS
          </span>
        </div>
      )}

      {/* Comparison Summary */}
      {comparison && (
        <ComparisonSummary comparison={comparison} language={language} />
      )}

      {/* Empty state hint */}
      {!profileA && !profileB && (
        <div className="card p-8 text-center bg-surface-800/30">
          <p className="text-text-tertiary text-sm">
            {t
              ? 'Configura los perfiles arriba y haz clic en "Generar Perfil" para ver la comparación.'
              : 'Configure the profiles above and click "Generate Profile" to see the comparison.'}
          </p>
        </div>
      )}
    </div>
  );
}

interface ResearchResultsTabProps {
  language: string;
  clientId: string;
}

interface ResearchResult {
  id: string;
  question: string;
  question_es: string;
  category: string;
  source: string;
  source_date: string;
  quality_score: number;
  results: Array<{ label: string; label_es: string; value: number; unit?: string }>;
}

interface ResearchData {
  has_data: boolean;
  client_id: string;
  total_questions: number;
  categories: string[];
  results: ResearchResult[];
}

function ResearchResultsTab({ language, clientId }: ResearchResultsTabProps) {
  const t = language === 'es';
  const [data, setData] = useState<ResearchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<string>>(new Set());

  // Fetch research data
  useEffect(() => {
    if (!clientId) return;
    setLoading(true);

    fetch(`http://localhost:3001/api/clients/${clientId}/consumer/research`)
      .then(res => res.json())
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clientId]);

  const toggleQuestion = (id: string) => {
    setExpandedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, { en: string; es: string }> = {
      consumption_occasions: { en: 'Consumption Occasions', es: 'Ocasiones de Consumo' },
      shopping_behavior: { en: 'Shopping Behavior', es: 'Comportamiento de Compra' },
      price_research: { en: 'Price Research', es: 'Investigación de Precios' },
    };
    return labels[category]?.[language as 'en' | 'es'] || category;
  };

  const getQualityBadge = (score: number) => {
    if (score >= 9) return { label: t ? 'Alta' : 'High', color: 'bg-green-500/20 text-green-400' };
    if (score >= 7) return { label: t ? 'Media' : 'Medium', color: 'bg-amber-500/20 text-amber-400' };
    return { label: t ? 'Estimada' : 'Estimated', color: 'bg-surface-600 text-text-tertiary' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Client Data Badge */}
      <div className="flex items-center gap-2">
        <span className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded border border-amber-500/30">
          {t ? 'Datos del Cliente' : 'Client Data'}
        </span>
        <span className="text-sm text-text-tertiary">
          {t
            ? 'Estos resultados son únicos para este cliente y no provienen del simulador general.'
            : 'These results are unique to this client and are not from the general simulator.'}
        </span>
      </div>

      {/* No data state */}
      {(!data || !data.has_data) && (
        <div className="card p-8 text-center border-2 border-dashed border-surface-600">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-surface-700 flex items-center justify-center">
            <svg className="w-6 h-6 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-white mb-2">
            {t ? 'Sin Datos de Investigación' : 'No Research Data'}
          </h3>
          <p className="text-text-secondary mb-4 max-w-md mx-auto">
            {t
              ? 'No hay datos de investigación específicos para este cliente. Los datos aparecerán cuando se carguen encuestas o estudios propietarios.'
              : 'No client-specific research data available. Data will appear when proprietary surveys or studies are loaded.'}
          </p>
        </div>
      )}

      {/* Research results */}
      {data?.has_data && (
        <>
          {/* Summary */}
          <div className="flex items-center gap-4 text-sm text-text-secondary">
            <span>{data.total_questions} {t ? 'preguntas de investigación' : 'research questions'}</span>
            <span>•</span>
            <span>{data.categories.length} {t ? 'categorías' : 'categories'}</span>
          </div>

          {/* Questions by category */}
          {data.categories.map(category => {
            const categoryResults = data.results.filter(r => r.category === category);
            return (
              <div key={category} className="space-y-3">
                <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
                  {getCategoryLabel(category)}
                </h3>

                {categoryResults.map(result => {
                  const isExpanded = expandedQuestions.has(result.id);
                  const quality = getQualityBadge(result.quality_score);

                  return (
                    <div key={result.id} className="card overflow-hidden">
                      {/* Question header */}
                      <button
                        onClick={() => toggleQuestion(result.id)}
                        className="w-full p-4 flex items-center justify-between text-left hover:bg-surface-700/50 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="font-medium text-white">
                            {t ? result.question_es : result.question}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-text-tertiary">
                            <span>{result.source}</span>
                            <span>•</span>
                            <span>{result.source_date}</span>
                            <span className={`px-1.5 py-0.5 rounded ${quality.color}`}>
                              {quality.label}
                            </span>
                          </div>
                        </div>
                        <svg
                          className={`w-5 h-5 text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Results (expanded) */}
                      {isExpanded && (
                        <div className="px-4 pb-4 border-t border-surface-700">
                          <div className="pt-4 space-y-2">
                            {result.results.map((item, i) => {
                              const maxValue = Math.max(...result.results.map(r => r.value));
                              const barWidth = (item.value / maxValue) * 100;

                              return (
                                <div key={i} className="flex items-center gap-3">
                                  <span className="w-32 text-sm text-text-secondary truncate">
                                    {t ? item.label_es : item.label}
                                  </span>
                                  <div className="flex-1 h-6 bg-surface-700 rounded overflow-hidden">
                                    <div
                                      className="h-full bg-primary-500/60 rounded"
                                      style={{ width: `${barWidth}%` }}
                                    />
                                  </div>
                                  <span className="w-16 text-sm text-white text-right font-medium">
                                    {item.value}{item.unit || ''}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
